"use client";

import { useMemo } from "react";
import {
  partitionMarketplaceSuggestions,
  resolveMarketplaceSearchPanelStatus,
  type MarketplaceSearchPanelStatus,
} from "./marketplaceSearchUtils";
import type { MarketplaceSearchResult } from "@/types/api/marketplace";

export function useMarketplaceSearchSuggestions({
  searchQuery,
  debouncedSearch,
  suggestions,
  isFetching,
  isFocused = true,
}: {
  searchQuery: string;
  debouncedSearch: string;
  suggestions: MarketplaceSearchResult[];
  isFetching: boolean;
  isFocused?: boolean;
}) {
  const { exact, related } = useMemo(() => {
    if (debouncedSearch.trim()) {
      // API already filters by `key` — show all returned suggestions as-is.
      return { exact: suggestions, related: [] as MarketplaceSearchResult[] };
    }

    return partitionMarketplaceSuggestions(
      suggestions,
      searchQuery.trim() ? searchQuery : debouncedSearch
    );
  }, [suggestions, debouncedSearch, searchQuery]);

  const status: MarketplaceSearchPanelStatus = useMemo(
    () =>
      resolveMarketplaceSearchPanelStatus({
        searchQuery,
        debouncedSearch,
        isFetching,
        exactCount: exact.length,
        relatedCount: related.length,
        isFocused,
      }),
    [searchQuery, debouncedSearch, isFetching, exact.length, related.length, isFocused]
  );

  const displayResults = status === "browse" ? suggestions : exact;
  const displayRelated = status === "related" ? related : [];
  const showPanel =
    isFocused &&
    (status === "browse" ||
      status === "typing" ||
      status === "loading" ||
      displayResults.length > 0 ||
      displayRelated.length > 0);

  const activeQuery =
    debouncedSearch.trim().length > 0 ? debouncedSearch : searchQuery;

  return {
    status,
    displayResults,
    displayRelated,
    showPanel,
    activeQuery,
    showSeeAll: activeQuery.trim().length > 0 && status !== "loading" && status !== "typing",
  };
}
